/**
 * Reconciliation history. BP.md §4: manual re-upload / re-run on a cadence the
 * user controls, typically weekly.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { RunListItem } from '@picklii/shared';
import { AppShell } from '../components/AppShell';
import { ConfirmAction } from '../components/ConfirmAction';
import { Icon } from '../components/Icon';
import { api } from '../lib/api';
import { money, shortDate } from '../lib/format';

export function Runs() {
	const navigate = useNavigate();
	const [runs, setRuns] = useState<RunListItem[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [deleting, setDeleting] = useState<string | null>(null);

	async function remove(id: string) {
		setDeleting(id);
		setError(null);
		try {
			await api.deleteRun(id);
			// Drop it locally rather than refetching: the list is already correct
			// once the row is gone, and a reload would flash the whole table.
			setRuns((current) => (current ?? []).filter((r) => r.id !== id));
		} catch (cause) {
			setError(cause instanceof Error ? cause.message : 'Could not delete that reconciliation.');
		} finally {
			setDeleting(null);
		}
	}

	useEffect(() => {
		api.listRuns().then(setRuns, (cause: Error) => setError(cause.message));
	}, []);

	return (
		<AppShell title="Reconciliations">
			<section className="col-span-12">
				{error !== null && (
					<div className="alert alert-error alert-soft" role="alert">
						<span>{error}</span>
					</div>
				)}

				{runs === null && error === null && (
					<div className="flex justify-center py-16">
						<span className="loading loading-spinner" />
					</div>
				)}

				{runs !== null && runs.length === 0 && (
					<div className="card bg-base-200 border-base-300 border shadow-xs">
						<div className="card-body items-center text-center">
							<Icon name="upload" className="size-8 opacity-70" />
							<h2 className="card-title text-base">No reconciliations yet</h2>
							<p className="text-base-content/70 max-w-md text-sm">
								Upload a sold-hours export and a PSA budget export. Picklii matches them on
								project and role, diffs them, and ranks what it finds by urgency.
							</p>
							<Link to="/runs/new" className="btn btn-primary btn-sm mt-2">
								Start the first one
							</Link>
						</div>
					</div>
				)}

				{runs !== null && runs.length > 0 && (
					<div className="card bg-base-200 border-base-300 border shadow-xs">
						<div className="card-body">
							<div className="overflow-x-auto">
								<table className="table">
									<thead>
										<tr>
											<th>Run</th>
											<th>Findings</th>
											<th>Critical / High</th>
											<th className="text-right">Exposure (measured)</th>
											<th className="text-right">Exposure (estimated)</th>
											<th>Run by</th>
											<th />
										</tr>
									</thead>
									<tbody>
										{runs.map((run) => {
											const total = Object.values(run.summary.findingCountsByType).reduce(
												(a, b) => a + b,
												0,
											);
											return (
												<tr
													key={run.id}
													// The row is the control. tabIndex + key handling keep it
													// reachable without a mouse, which a bare onClick would not.
													tabIndex={0}
													role="link"
													aria-label={`Open reconciliation from ${shortDate(run.createdAt)}`}
													className="hover:bg-base-300 focus-visible:bg-base-300 cursor-pointer transition-colors"
													onClick={() => navigate(`/runs/${run.id}`)}
													onKeyDown={(e) => {
														if (e.key === 'Enter' || e.key === ' ') {
															e.preventDefault();
															navigate(`/runs/${run.id}`);
														}
													}}>
													<td className="font-medium">{shortDate(run.createdAt)}</td>
													<td className="tabular-nums">{total}</td>
													<td>
														<span className="badge badge-error badge-sm me-1">
															{run.summary.findingCountsByBand.Critical}
														</span>
														<span className="badge badge-warning badge-sm">
															{run.summary.findingCountsByBand.High}
														</span>
													</td>
													<td className="text-right tabular-nums">
														{money(run.summary.exactExposureCents)}
													</td>
													<td className="text-right tabular-nums">
														{money(run.summary.estimatedExposureCents)}
													</td>
													{/* Who produced a financial finding is part of the record. */}
													<td className="text-base-content/70">{run.createdBy.email}</td>
													{/*
													 * The row navigates on click, so this cell must swallow
													 * the event or deleting would also open the thing being
													 * deleted. Same for keyboard: Enter on the row opens it.
													 */}
													<td
														className="text-right"
														onClick={(e) => e.stopPropagation()}
														onKeyDown={(e) => e.stopPropagation()}>
														<ConfirmAction
															compact
															label="Delete"
															confirmLabel={`Delete the ${shortDate(run.createdAt)} reconciliation?`}
															description={`Run by ${run.createdBy.email}. This removes the run and every finding under it.`}
															busy={deleting === run.id}
															onConfirm={() => void remove(run.id)}
														/>
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				)}
			</section>
		</AppShell>
	);
}
